import { useState, useEffect } from 'react';
import { 
  doc, 
  updateDoc, 
  getDoc, 
  setDoc 
} from 'firebase/firestore';
import { 
  ref, 
  uploadBytes, 
  getDownloadURL, 
  deleteObject 
} from 'firebase/storage';
import { updateProfile } from 'firebase/auth';
import { toast } from 'react-toastify';
import { db, storage } from '../auth/firebase';
import { useAuth } from '../contexts/AuthContext';

interface UserProfile {
  uid: string;
  email: string;
  displayName: string;
  photoURL: string;
  bio?: string;
  location?: string;
  joinedAt: Date;
  reportsCount: number;
  emailNotifications: boolean;
}

export const useProfile = () => {
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const { currentUser } = useAuth();

  // Load user profile
  useEffect(() => {
    const loadProfile = async () => {
      if (!currentUser) {
        setProfile(null);
        setLoading(false);
        return;
      }

      try {
        const profileDoc = await getDoc(doc(db, 'userProfiles', currentUser.uid));
        
        if (profileDoc.exists()) {
          const data = profileDoc.data();
          setProfile({
            uid: currentUser.uid,
            email: currentUser.email || '',
            displayName: data.displayName || currentUser.displayName || '',
            photoURL: data.photoURL || currentUser.photoURL || '',
            bio: data.bio || '',
            location: data.location || '',
            joinedAt: data.joinedAt?.toDate() || new Date(),
            reportsCount: data.reportsCount || 0,
            emailNotifications: data.emailNotifications !== false // Default to true
          });
        } else {
          // Create initial profile
          const newProfile: UserProfile = {
            uid: currentUser.uid,
            email: currentUser.email || '',
            displayName: currentUser.displayName || '',
            photoURL: currentUser.photoURL || '',
            bio: '',
            location: '',
            joinedAt: new Date(),
            reportsCount: 0,
            emailNotifications: true
          };
          
          await setDoc(doc(db, 'userProfiles', currentUser.uid), {
            ...newProfile,
            joinedAt: new Date()
          });
          
          setProfile(newProfile);
        }
      } catch (err) {
        // Error handled silently for better UX
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [currentUser]);

  // Upload profile picture
  const uploadProfilePicture = async (file: File): Promise<string> => {
    if (!currentUser) {
      throw new Error('User must be logged in');
    }

    setUploading(true);
    try {
      // Delete old profile picture if exists
      if (profile?.photoURL && profile.photoURL.includes('firebase')) {
        try {
          const oldRef = ref(storage, profile.photoURL);
          await deleteObject(oldRef);
        } catch (error) {
          console.warn('Could not delete old profile picture:', error);
        }
      }

      // Upload new picture
      const storageRef = ref(storage, `profile-pictures/${currentUser.uid}/${Date.now()}_${file.name}`);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);

      // Update Firebase Auth profile
      await updateProfile(currentUser, {
        photoURL: downloadURL
      });

      // Update Firestore profile
      await updateDoc(doc(db, 'userProfiles', currentUser.uid), {
        photoURL: downloadURL
      });

      // Update local state
      setProfile(prev => prev ? { ...prev, photoURL: downloadURL } : null);

      toast.success('Profile picture updated successfully!');
      return downloadURL;
    } catch (err) {
      throw new Error('Failed to upload profile picture');
    } finally {
      setUploading(false);
    }
  };

  // Update profile information
  const updateProfileInfo = async (updates: Partial<Pick<UserProfile, 'displayName' | 'bio' | 'location' | 'emailNotifications'>>) => {
    if (!currentUser || !profile) {
      throw new Error('User must be logged in');
    }

    try {
      // Update Firebase Auth if displayName changed
      if (updates.displayName && updates.displayName !== profile.displayName) {
        await updateProfile(currentUser, {
          displayName: updates.displayName
        });
      }

      // Update Firestore profile
      await updateDoc(doc(db, 'userProfiles', currentUser.uid), updates);

      // Update local state
      setProfile(prev => prev ? { ...prev, ...updates } : null);

      toast.success('Profile updated successfully!');
    } catch (err) {
      throw new Error('Failed to update profile');
    }
  };

  // Increment reports count
  const incrementReportsCount = async () => {
    if (!currentUser || !profile) return;

    try {
      const newCount = profile.reportsCount + 1;
      await updateDoc(doc(db, 'userProfiles', currentUser.uid), {
        reportsCount: newCount
      });

      setProfile(prev => prev ? { ...prev, reportsCount: newCount } : null);
    } catch (error) {
      console.error('Error updating reports count:', error);
    }
  };

  return {
    profile,
    loading,
    uploading,
    uploadProfilePicture,
    updateProfileInfo,
    incrementReportsCount
  };
};
